/// <reference types="react-scripts" />
import { Box, Divider, MenuList, Paper, Typography } from "@mui/material";
import * as React from "react";
import { Base } from "ui/Scripts/source/framework/base";
import { ICompanyMenuItem } from "ui/Scripts/source/models/company/companyMenuItem";
import { IInstantMessageListItem } from "ui/Scripts/source/models/instantMessage/instantMessageListItem";
import { SignalRConnectionManager } from "ui/Scripts/source/models/signalR/signalRConnectionManager";
import { Translations } from "ui/Scripts/source/models/translations";
import LogoEsalkku from "../../../../../Content/devel/images/logo_eSalkku.png";
import { AppUtils } from "../../../framework/appUtils";
import { customHistory } from "../../../framework/customHistory";
import { IAppSessionState } from "../../../models/store/storeTypes";
import { PageNavMenuCompanyItem } from "../../company/Company/PageNavMenuItemCompany";
import { SectionIcon, SectionType } from "../../framework/Icon/SectionIcon";
import { HeaderItem } from "./HeaderItem";
import { ExternalSystemDropdown } from "./ExternalSystemDropdown";

/* eslint-disable no-unused-vars */
export const enum HeaderMenuItem {
    Front = "0",
    CurrentTopics = "1",
    Documents = "2",
    Events = "3",
    Tasks = "4",
    UserRecords = "5",
    CompanyGroups = "6",
    Companies = "7",
    UserGroups = "8",
    Users = "9",
    Log = "10",
    ExternalSystems = "11",
    InstantMessages = "12",
    WhistleBlow = "13",
    CalendarTasks = "14",
    Reports = "15",
    Risks = "16",
}
/* eslint-enable no-unused-vars */

export interface INavigationMenuProp {
    appState: IAppSessionState;
    activeMenuItem: HeaderMenuItem;
    companies?: ICompanyMenuItem[];
    activeCompanyId?: string;
    onNavigateOutCheck?: () => Promise<boolean>;
    onChangeActiveCompany?: (companyId: string) => void;
}

export const NavigationMenu = (props: INavigationMenuProp) => {
    const signalRConnectionManager = React.useRef<SignalRConnectionManager>(null);

    const updateInstantMessageListLine = (item: IInstantMessageListItem) => {
        if (!item || props.activeMenuItem === HeaderMenuItem.InstantMessages) return;
        const newInstantMessages = props.appState.newInstantMessages.filter(i => i.id !== item.id);
        newInstantMessages.push(item);
        AppUtils.setNewInstantMessages(newInstantMessages);
    };

    React.useEffect(() => {
        //SignalR
        signalRConnectionManager.current = new SignalRConnectionManager();
        signalRConnectionManager.current.openConnection(appConfig.rootUrl, {
            onUpdateInstantMessageListLine: updateInstantMessageListLine
        });
        return () => {
            //SignalR
            signalRConnectionManager.current.closeConnection();
        };
    }, []);

    const canNavigateOut = async (): Promise<boolean> => {
        if (!props.onNavigateOutCheck) return Base.getPromiseResult(true);
        return await props.onNavigateOutCheck();
    };

    const handleGoToPage = async (path: string) => {
        const result = await canNavigateOut();
        if (!result) return;
        customHistory.push(path);
    };

    const handleGoToFrontPage = async () => {
        handleGoToPage("/front");
    };

    const handleGoToMessagePage = async () => {
        handleGoToPage("/new/message");
    };

    const handleGoToDocumentPage = async () => {
        handleGoToPage("/new/document");
    };

    const handleGoToEventPage = async () => {
        handleGoToPage("/new/event");
    };

    const handleGoToTaskPage = async () => {
        handleGoToPage("/new/task");
    };

    const handleGoToRecordPage = async () => {
        handleGoToPage("/new/record");
    };

    const handleGoToCompanyGroupPage = async () => {
        handleGoToPage("/new/companygroup");
    };
    
    const handleGoToCompaniesPage = async () => {
        handleGoToPage("/new/company");
    };
    
    const handleGoToUserGroupsPage = async () => {
        handleGoToPage("/new/usergroup");
    };

    const handleGoToUsersPage = async () => {
        handleGoToPage("/new/user");
    };

    const handleGoToLogPage = async () => {
        handleGoToPage("/new/log");
    };

    const handleGoToInstantMessagePage = async () => {
        handleGoToPage("/new/instantmessage");
    };

    const handleGoToWhistleBlowPage = async () => {
        handleGoToPage("/new/whistleblow");
    };

    const handleGoToCalendarTaskPage = async () => {
        handleGoToPage("/new/calendar");
    };

    const handleGoToReportsPage = async () => {
        handleGoToPage("/new/report");
    };

    const handleGoToRisksPage = async () => {
        handleGoToPage("/new/risk");
    };

    const sessionState = props.appState?.state;

    if (!sessionState) return null;

    const showExternalSystems = (sessionState.intraModules.length > 0 || sessionState.intraRestModules.length > 0 || sessionState.externalSystems.length > 0);

    return (
        <Paper elevation={0} sx={{ width: "192px", height: "100vh", padding: ".5rem 0 1rem 0", position: "fixed", display: "flex", borderRadius: 0, flexDirection: "column" }}>
            <Box display="flex" justifyContent="center" alignItems="center" sx={{ paddingBottom: "1rem" }}>
                <Box component="img" alt="logo" src={LogoEsalkku} onClick={handleGoToFrontPage} sx={{ maxHeight: "48px", maxWidth: "192px" }} />
            </Box>
            <Box display="flex" flexDirection="column" justifyContent="space-between" height="100%">
                <Box display="flex" flexDirection="column">
                    <Box sx={{ height: "76px" }}>
                        {props.companies && (
                            <PageNavMenuCompanyItem
                                companies={props.companies}
                                activeCompanyId={props.activeCompanyId}
                                onChangeActiveCompany={props.onChangeActiveCompany}
                            />
                        )}
                    </Box>
                    <Typography fontWeight={600} sx={{ padding: "0 16px" }}>{Translations.Menu}</Typography>
                    <MenuList>
                        <HeaderItem
                            active={props.activeMenuItem === HeaderMenuItem.Front}
                            icon={<SectionIcon type={SectionType.Front} />}
                            text={Translations.FrontPage}
                            onClick={handleGoToFrontPage}
                        />
                        {sessionState.canUseMessages &&
                            <HeaderItem
                                active={props.activeMenuItem === HeaderMenuItem.CurrentTopics}
                                icon={<SectionIcon type={SectionType.CurrentTopics} />}
                                text={Translations.CurrentTopics}
                                onClick={handleGoToMessagePage}
                            />
                        }
                        {sessionState.canUseCalendarTasks &&
                            <HeaderItem
                                active={props.activeMenuItem === HeaderMenuItem.CalendarTasks}
                                icon={<SectionIcon type={SectionType.CalendarTasks} />}
                                text={Translations.Calendar}
                                onClick={handleGoToCalendarTaskPage}
                            />
                        }
                        {sessionState.canUseInstantMessages &&
                            <HeaderItem
                                active={props.activeMenuItem === HeaderMenuItem.InstantMessages}
                                icon={<SectionIcon type={SectionType.InstantMessages} />}
                                text={Translations.InstantMessagesMenuTitle}
                                eventsExist={props.appState.newInstantMessages.length > 0}
                                onClick={handleGoToInstantMessagePage}
                            />
                        }
                        {sessionState.canUseDocuments &&
                            <HeaderItem
                                active={props.activeMenuItem === HeaderMenuItem.Documents}
                                icon={<SectionIcon type={SectionType.Documents} />}
                                text={Translations.Documents}
                                onClick={handleGoToDocumentPage}
                            />
                        }
                        {sessionState.canUseEvents &&
                            <HeaderItem
                                active={props.activeMenuItem === HeaderMenuItem.Events}
                                icon={<SectionIcon type={SectionType.Events} />}
                                text={Translations.Events}
                                onClick={handleGoToEventPage}
                            />
                        }
                        {sessionState.canUseTasks &&
                            <HeaderItem
                                active={props.activeMenuItem === HeaderMenuItem.Tasks}
                                icon={<SectionIcon type={SectionType.Tasks} />}
                                text={Translations.Tasks}
                                onClick={handleGoToTaskPage}
                            />
                        }
                        {(sessionState.canUseRecords || sessionState.canUseOwnRecord) &&
                            <HeaderItem
                                active={props.activeMenuItem === HeaderMenuItem.UserRecords}
                                icon={<SectionIcon type={SectionType.UserRecords} />}
                                text={sessionState.canUseRecords ? Translations.Resources : Translations.RecordUser}
                                onClick={handleGoToRecordPage}
                            />
                        }
                        {(sessionState.canUseReports) &&
                            <HeaderItem
                                active={props.activeMenuItem === HeaderMenuItem.Reports}
                                icon={<SectionIcon type={SectionType.Reports} />}
                                text={Translations.Reports}
                                onClick={handleGoToReportsPage}
                            />
                        }
                        {(sessionState.canUseRisks) &&
                            <HeaderItem
                                active={props.activeMenuItem === HeaderMenuItem.Risks}
                                icon={<SectionIcon type={SectionType.Risks} />}
                                text={Translations.RiskManagement}
                                onClick={handleGoToRisksPage}
                            />
                        }
                        {sessionState.canUseCompanyGroups &&
                            <HeaderItem
                                active={props.activeMenuItem === HeaderMenuItem.CompanyGroups}
                                icon={<SectionIcon type={SectionType.CompanyGroups} />}
                                text={Translations.CompanyGroups}
                                onClick={handleGoToCompanyGroupPage}
                            />
                        }
                        {sessionState.canUseCompanies &&
                            <HeaderItem
                                active={props.activeMenuItem === HeaderMenuItem.Companies}
                                icon={<SectionIcon type={SectionType.Companies} />}
                                text={sessionState.isSuperAdmin ? Translations.Companies : Translations.Company}
                                onClick={handleGoToCompaniesPage}
                            />
                        }
                        {sessionState.canUseUserGroups &&
                            <HeaderItem
                                active={props.activeMenuItem === HeaderMenuItem.UserGroups}
                                icon={<SectionIcon type={SectionType.UserGroups} />}
                                text={Translations.UserGroups}
                                onClick={handleGoToUserGroupsPage}
                            />
                        }
                        {sessionState.canUseUsers &&
                            <HeaderItem
                                active={props.activeMenuItem === HeaderMenuItem.Users}
                                icon={<SectionIcon type={SectionType.Users} />}
                                text={Translations.Users}
                                onClick={handleGoToUsersPage}
                            />
                        }
                        {sessionState.canUseLogs &&
                            <HeaderItem
                                active={props.activeMenuItem === HeaderMenuItem.Log}
                                icon={<SectionIcon type={SectionType.Log} />}
                                text={Translations.Log}
                                onClick={handleGoToLogPage}
                            />
                        }
                        {sessionState.canUseWhistleBlow &&
                            <HeaderItem
                                active={props.activeMenuItem === HeaderMenuItem.WhistleBlow}
                                icon={<SectionIcon type={SectionType.WhistleBlow} />}
                                text={Translations.WhistleBlow}
                                onClick={handleGoToWhistleBlowPage}
                            />
                        }
                        {showExternalSystems &&
                            <Divider />
                        }
                        {showExternalSystems &&
                            <ExternalSystemDropdown
                                text={Translations.ExternalSystems}
                                intraModules={sessionState.intraModules}
                                intraRestModules={sessionState.intraRestModules}
                                externalSystems={sessionState.externalSystems}
                            />
                        }
                    </MenuList>
                </Box>
            </Box>
        </Paper>
    );
};
